/**
 * Утилиты для работы с деревом Query Builder
 * Чистые функции без side effects
 */

import { defaultLogicalOperator } from "../types/ZQueryBuilderConstants";
import {
  isGroupNode,
  ZCriterionNode,
  ZGroupNode,
  ZLogicalOperator,
  ZNodePath,
  ZQueryBuilderNode,
} from "../types/ZQueryBuilderTypes";

export { isGroupNode };

/**
 * Генерация уникального ID для узла
 */
export const generateNodeId = (): string => crypto.randomUUID();

/**
 * Поиск узла по пути в дереве
 * @param root - корневая группа
 * @param path - путь к узлу (массив индексов)
 * @returns найденный узел или null
 */
export const findNodeByPath = (
  root: ZGroupNode,
  path: ZNodePath,
): ZQueryBuilderNode | null => {
  if (path.length === 0) {
    return root;
  }

  let current: ZQueryBuilderNode = root;

  let pathIndex = 0;
  while (pathIndex < path.length) {
    const index = path[pathIndex] || 0;

    if (!isGroupNode(current)) {
      return null; // Критерий не имеет items
    }

    if (index < 0 || index >= current.items.length) {
      return null; // Индекс вне диапазона
    }

    const nextNode: ZQueryBuilderNode | undefined = current.items[index];
    if (!nextNode) {
      return null;
    }
    current = nextNode;
    pathIndex += 1;
  }

  return current;
};

/**
 * Получение родительской группы для узла по пути
 * @param root - корневая группа
 * @param path - путь к узлу
 * @returns родительская группа или null
 */
export const getParentGroup = (
  root: ZGroupNode,
  path: ZNodePath,
): ZGroupNode | null => {
  if (path.length === 0) {
    return null; // Корневая группа не имеет родителя
  }

  const parentPath = path.slice(0, -1);
  const parent = findNodeByPath(root, parentPath);

  return parent && isGroupNode(parent) ? parent : null;
};

/**
 * Создание пустой группы
 * @param op - логический оператор (по умолчанию AND)
 * @returns новая группа без items
 */
export const createEmptyGroup = (
  op: ZLogicalOperator = defaultLogicalOperator,
): ZGroupNode => ({
  type: "group",
  id: generateNodeId(),
  op,
  items: [],
});

/**
 * Создание пустого критерия
 * @returns новый критерий с незаполненными полями
 */
export const createCriterion = (): ZCriterionNode => ({
  type: "criterion",
  id: generateNodeId(),
  fieldMeta: null,
  valueType: null,
  operator: null,
  value: null,
});

/**
 * Проверка, можно ли переместить узел вверх
 * @param root - корневая группа
 * @param path - путь к узлу
 * @returns true если перемещение возможно
 */
export const canMoveUp = (root: ZGroupNode, path: ZNodePath): boolean => {
  if (path.length === 0) return false;
  const index = path[path.length - 1];
  if (index === undefined || index <= 0) return false;
  return findNodeByPath(root, path) !== null;
};

/**
 * Проверка, можно ли переместить узел вниз
 * @param root - корневая группа
 * @param path - путь к узлу
 * @returns true если перемещение возможно
 */
export const canMoveDown = (root: ZGroupNode, path: ZNodePath): boolean => {
  const parent = getParentGroup(root, path);
  if (!parent) return false;

  const index = path[path.length - 1];
  return index !== undefined && index >= 0 && index < parent.items.length - 1;
};
